import React, { useEffect, useMemo, useState } from "react";
import { useAusencias, Ausencia, TipoAusencia, TIPO_AUSENCIA_LABEL, TIPO_AUSENCIA_COLOR, AusenciaDocumento } from "@/contexts/AusenciasContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Lock, AlertTriangle, CheckCircle2, Clock, Trash2, Pencil, Paperclip, Upload, X } from "lucide-react";
import { format, differenceInBusinessDays } from "date-fns";
import { toast } from "@/hooks/use-toast";

const TIPOS: TipoAusencia[] = ["vacaciones", "baja_medica", "asuntos_propios", "formacion"];

export default function MisAusencias() {
  const { user } = useAuth();
  const { ausencias, getSaldo, modoComputo, createAusencia, updateAusencia, deleteAusencia, listDocumentos, uploadDocumento, deleteDocumento, getSignedUrl, refresh } = useAusencias();
  const modoLabel = modoComputo === "naturales" ? "Días naturales" : "Días laborables";

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Ausencia | null>(null);
  const [tipo, setTipo] = useState<TipoAusencia>("vacaciones");
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [motivo, setMotivo] = useState("");
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [docs, setDocs] = useState<AusenciaDocumento[]>([]);
  const [saving, setSaving] = useState(false);

  const misAusencias = useMemo(
    () => ausencias.filter((a) => a.tecnico_id === user?.id),
    [ausencias, user?.id]
  );

  const anioActual = new Date().getFullYear();
  const saldo = user ? getSaldo(user.id, anioActual) : { totales: 0, consumidos: 0, pendientes: 0, restantes: 0 };

  const diasEstimados = useMemo(() => {
    if (!fechaInicio || !fechaFin) return 0;
    try {
      const i = new Date(fechaInicio);
      const f = new Date(fechaFin);
      if (f < i) return 0;
      if (modoComputo === "naturales") {
        return Math.floor((f.getTime() - i.getTime()) / 86400000) + 1;
      }
      return differenceInBusinessDays(f, i) + 1;
    } catch {
      return 0;
    }
  }, [fechaInicio, fechaFin, modoComputo]);

  const resetForm = () => {
    setEditing(null);
    setTipo("vacaciones");
    setFechaInicio("");
    setFechaFin("");
    setMotivo("");
    setPendingFiles([]);
    setDocs([]);
  };

  const openCreate = () => {
    resetForm();
    setOpen(true);
  };

  const openEdit = async (a: Ausencia) => {
    setEditing(a);
    setTipo(a.tipo);
    setFechaInicio(a.fecha_inicio);
    setFechaFin(a.fecha_fin);
    setMotivo(a.motivo || "");
    setPendingFiles([]);
    const ds = await listDocumentos(a.id);
    setDocs(ds);
    setOpen(true);
  };

  const isBlocked = (a: Ausencia) => {
    if (a.estado_validacion === "rechazada") return false;
    if (a.estado_validacion === "aprobada") return true;
    const dentroVentana = Date.now() - new Date(a.created_at).getTime() < 15 * 60 * 1000;
    return !dentroVentana;
  };

  const handleSave = async () => {
    if (!fechaInicio || !fechaFin) {
      toast({ title: "Faltan fechas", variant: "destructive" });
      return;
    }
    if (tipo === "baja_medica" && pendingFiles.length === 0 && docs.length === 0) {
      toast({ title: "Justificante obligatorio", description: "Sube el parte de baja médica", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      let ausenciaId = editing?.id;
      if (editing) {
        const patch: any = { tipo, fecha_inicio: fechaInicio, fecha_fin: fechaFin, motivo: motivo || null };
        if (editing.estado_validacion === "rechazada") {
          patch.estado_validacion = "pendiente";
          patch.motivo_rechazo = null;
        }
        await updateAusencia(editing.id, patch);
      } else {
        const created = await createAusencia({
          tipo,
          fecha_inicio: fechaInicio,
          fecha_fin: fechaFin,
          motivo: motivo || null,
        } as any);
        ausenciaId = created?.id;
      }
      if (ausenciaId && pendingFiles.length > 0) {
        for (const f of pendingFiles) {
          await uploadDocumento(ausenciaId, f);
        }
      }
      await refresh();
      setOpen(false);
      resetForm();
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDoc = async (doc: AusenciaDocumento) => {
    await deleteDocumento(doc);
    setDocs((prev) => prev.filter((d) => d.id !== doc.id));
  };

  const handleViewDoc = async (doc: AusenciaDocumento) => {
    const url = await getSignedUrl(doc.url_archivo);
    if (url) window.open(url, "_blank");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Mis ausencias</h1>
          <p className="text-sm text-muted-foreground">Vacaciones, bajas y permisos</p>
        </div>
        <Button onClick={openCreate} className="gap-2"><Plus className="h-4 w-4" /> Nueva ausencia</Button>
      </div>

      {/* Saldo vacaciones */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <CardTitle className="text-base">Vacaciones {anioActual}</CardTitle>
            <Badge variant="secondary" className="text-xs">{modoLabel}</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Disponibles" value={`${saldo.restantes} d`} highlight />
            <Stat label="Totales" value={`${saldo.totales} d`} />
            <Stat label="Consumidas" value={`${saldo.consumidos} d`} />
            <Stat label="Pendientes" value={`${saldo.pendientes} d`} />
          </div>
        </CardContent>
      </Card>

      <div className="space-y-2">
        {misAusencias.length === 0 && (
          <Card><CardContent className="py-8 text-center text-muted-foreground">Aún no has registrado ausencias</CardContent></Card>
        )}
        {misAusencias.map((a) => {
          const blocked = isBlocked(a);
          return (
            <Card key={a.id}>
              <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                <div className="flex items-start gap-3 min-w-0">
                  {blocked && <Lock className="h-4 w-4 text-muted-foreground mt-1" />}
                  {a.estado_validacion === "aprobada" && <CheckCircle2 className="h-4 w-4 text-green-600 mt-1" />}
                  {a.estado_validacion === "rechazada" && <AlertTriangle className="h-4 w-4 text-destructive mt-1" />}
                  {a.estado_validacion === "pendiente" && <Clock className="h-4 w-4 text-amber-600 mt-1" />}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={TIPO_AUSENCIA_COLOR[a.tipo]}>{TIPO_AUSENCIA_LABEL[a.tipo]}</Badge>
                      <span className="font-medium">
                        {format(new Date(a.fecha_inicio), "dd/MM/yyyy")} – {format(new Date(a.fecha_fin), "dd/MM/yyyy")}
                      </span>
                      <span className="text-sm text-muted-foreground">({Number(a.dias_solicitados)} días)</span>
                    </div>
                    {a.motivo && <p className="text-sm text-muted-foreground mt-1">{a.motivo}</p>}
                    {a.estado_validacion === "rechazada" && a.motivo_rechazo && (
                      <p className="text-sm text-destructive mt-1 font-medium">Motivo de rechazo: {a.motivo_rechazo}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => openEdit(a)} disabled={blocked} className="gap-1">
                    <Pencil className="h-3.5 w-3.5" /> {a.estado_validacion === "rechazada" ? "Corregir" : "Editar"}
                  </Button>
                  {!blocked && (
                    <Button size="sm" variant="ghost" onClick={() => deleteAusencia(a.id)}>
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar ausencia" : "Nueva ausencia"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {editing?.estado_validacion === "rechazada" && (
              <div className="rounded-lg border-2 border-destructive bg-destructive/10 p-3 text-sm">
                <p className="font-semibold text-destructive flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Ausencia rechazada</p>
                {editing.motivo_rechazo && <p className="mt-1">Motivo: {editing.motivo_rechazo}</p>}
                <p className="mt-1 text-xs text-muted-foreground">Al guardar volverá a estado pendiente.</p>
              </div>
            )}
            <div>
              <Label>Tipo</Label>
              <Select value={tipo} onValueChange={(v) => setTipo(v as TipoAusencia)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIPOS.map((t) => <SelectItem key={t} value={t}>{TIPO_AUSENCIA_LABEL[t]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Desde</Label>
                <Input type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} />
              </div>
              <div>
                <Label>Hasta</Label>
                <Input type="date" value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} />
              </div>
            </div>
            {diasEstimados > 0 && (
              <p className="text-sm text-muted-foreground">{diasEstimados} {modoComputo === "naturales" ? "días naturales" : "días laborables"}</p>
            )}
            {tipo === "vacaciones" && diasEstimados > saldo.restantes && (
              <p className="text-sm text-amber-600 dark:text-amber-400">
                ⚠️ Excede tu saldo disponible ({saldo.restantes} días)
              </p>
            )}
            <div>
              <Label>Motivo / observaciones</Label>
              <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={2} />
            </div>
            <div className="space-y-2">
              <Label>
                Justificante {tipo === "baja_medica" && <span className="text-destructive">*obligatorio</span>}
              </Label>
              {docs.map((d) => (
                <div key={d.id} className="flex items-center justify-between border rounded p-2 text-sm">
                  <button onClick={() => handleViewDoc(d)} className="text-left underline truncate flex items-center gap-1">
                    <Paperclip className="h-3 w-3" /> {d.nombre_archivo}
                  </button>
                  <Button size="sm" variant="ghost" onClick={() => handleDeleteDoc(d)}><X className="h-3 w-3" /></Button>
                </div>
              ))}
              {pendingFiles.map((f, i) => (
                <div key={i} className="flex items-center justify-between border rounded p-2 text-sm bg-muted/50">
                  <span className="truncate flex items-center gap-1"><Paperclip className="h-3 w-3" /> {f.name}</span>
                  <Button size="sm" variant="ghost" onClick={() => setPendingFiles((p) => p.filter((_, j) => j !== i))}><X className="h-3 w-3" /></Button>
                </div>
              ))}
              <label className="flex items-center justify-center gap-2 border-2 border-dashed rounded p-3 cursor-pointer hover:bg-muted/50 text-sm">
                <Upload className="h-4 w-4" />
                Adjuntar archivo
                <input
                  type="file"
                  className="hidden"
                  accept="image/*,application/pdf"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) setPendingFiles((p) => [...p, f]);
                    e.target.value = "";
                  }}
                />
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving}>
              {editing ? "Guardar cambios" : "Solicitar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Stat = ({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) => (
  <div className={`rounded-md border p-3 ${highlight ? "bg-primary/5 border-primary/30" : ""}`}>
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className={`text-xl font-bold ${highlight ? "text-primary" : ""}`}>{value}</p>
  </div>
);
